/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import { useCheckboxGroupContext } from './checkbox-group'
import CheckboxSvgIcon from './checkbox-icon'
import type {
  CheckboxComponents,
  CheckboxProps,
  CheckboxStatefulThemeProps
} from './types'
import { useCheckbox } from './use-checkbox'
import type { InputColorState } from '../../styles-system/input-element-utils'
import { resolveInputOutline } from '../../styles-system/input-element-utils'
import type {
  CSSOthersObject,
  Theme,
  ThemeUICSSObject
} from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import extractStringNodes from '../../utils/extract-string-nodes'
import { callAll } from '../../utils/function'
import { EMPTY_OBJECT, get } from '../../utils/object'
import { resolveSize } from '../../utils/size-utils'
import { resolveColor } from '../../utils/styled-props'
import type { StringOrNumber } from '../../utils/types'
import { formikField } from '../_adaptors'
import { BoxPrivate } from '../_primitives/box/box'
import { FlexPrivate } from '../_primitives/flex/flex'
import FlexInLine from '../_primitives/flex/flex-inline'
import Icon from '../_primitives/icon/icon'
import { InputBase } from '../_primitives/input/input'
import type { InputProps } from '../_primitives/input/types'
import Text from '../_primitives/text'

const CheckboxInputBase = React.forwardRef<
  HTMLInputElement,
  InputProps & CheckboxStatefulThemeProps
>(function _CheckboxInputBase(props, ref) {
  // @ts-ignore
  return <InputBase ref={ref} {...props} />
})

export type CheckPropsPrivate = CheckboxProps & { theme?: Theme }

const textWrapperSx: ThemeUICSSObject = {
  overflow: 'hidden'
}

const CheckboxPrivate: FC<CheckPropsPrivate> = React.forwardRef<
  HTMLInputElement,
  CheckPropsPrivate
>(function _CheckboxPrivate(props, ref) {
  const group = useCheckboxGroupContext()

  const mergedProps = { ...group, ...props } as CheckboxProps

  const {
    className,
    readOnly,
    disabled = readOnly || group?.disabled,
    checked: isCheckedProp,
    indeterminate: isIndeterminate,
    label: labelProp,
    labelIndicator,
    labelLocation = 'right',
    children,
    state: stateProps,
    size,
    variant,
    checkIcon: CheckIcon = <CheckboxSvgIcon />,
    iconSize: iconSizeProps,
    iconColor,
    onChange: onChangeProp,
    iconBg: iconBgProps,
    bg,
    background,
    backgroundColor,
    isInvalid,
    disableColor,
    disableBg,
    spacing: gap = 'sm',
    labelProps: _labelProps = EMPTY_OBJECT,
    active,
    hover,
    width = 'auto',
    minWidth = '0',
    ...rest
  } = mergedProps

  const theme = useSafeThemeUI(props)
  const fontSize = size
    ? resolveSize(size, get(theme, 'sizes.font'))
    : undefined
  const state = isInvalid ? 'danger' : stateProps
  const iconSize = iconSizeProps
    ? resolveSize(iconSizeProps, get(theme, 'sizes.icon'))
    : '1em'
  const iconBg: InputColorState | undefined = (iconBgProps ||
    bg ||
    backgroundColor ||
    background) as InputColorState | undefined
  let isChecked = isCheckedProp
  if (group?.value && mergedProps.value) {
    isChecked = group.value.includes(mergedProps.value as StringOrNumber)
  }
  const stringNodes = extractStringNodes(
    React.Children.toArray(children).filter(i => typeof i === 'string')
  )

  const renderNodes = React.Children.toArray(children).filter(
    i =>
      typeof i !== 'string' &&
      typeof i !== 'undefined' &&
      typeof i !== 'number' &&
      typeof i !== 'boolean'
  )

  const label =
    labelProp ?? (stringNodes.length > 0 ? `${stringNodes}` : undefined)

  let onChange = onChangeProp
  if (group?.onChange && mergedProps.value) {
    onChange = callAll(group.onChange, onChangeProp)
  }

  const {
    state: inputState,
    inputProps,
    checkboxProps,
    labelProps,
    rootProps
  } = useCheckbox({
    forwardedRef: ref,
    'aria-label': label,
    ...rest,
    isDisabled: disabled,
    isChecked,
    isInvalid,
    isIndeterminate,
    onChange,
    labelProps: _labelProps
  })

  const controllerStyle = useMemo(
    () =>
      ({
        ...(props.checkIcon
          ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }
          : {}),
        appearance: 'none',
        bg: iconBg ? iconBg : 'white',
        ...(iconColor ? { color: iconColor } : {}),
        borderRadius: '0.2em',
        ...resolveInputOutline(theme, state ? state : 'border'),
        height: iconSize,
        position: 'relative',
        minWidth: iconSize,
        width: iconSize
      }) as CSSOthersObject,
    [iconBg, iconColor, iconSize, state, theme, props.checkIcon]
  )

  const iconStyles = useMemo(
    () =>
      ({
        opacity: inputState.isChecked || inputState.isIndeterminate ? 1 : 0,
        transform:
          inputState.isChecked || inputState.isIndeterminate
            ? 'scale(1)'
            : 'scale(0.95)',
        transition: 'transform 200ms'
      }) as CSSOthersObject,
    [inputState.isChecked, inputState.isIndeterminate]
  )

  const clonedIcon = React.cloneElement(CheckIcon as any, {
    __css: iconStyles,
    isIndeterminate: variant !== 'switch' && inputState.isIndeterminate,
    isChecked: variant !== 'switch' && inputState.isChecked
  })

  const innerContent = useMemo(
    () =>
      renderNodes.length > 0 ? (
        renderNodes
      ) : label ? (
        <Text className="csg-tui-checkbox-label" ellipsis {...labelProps}>
          {label}
        </Text>
      ) : null,
    [labelProps, label, renderNodes]
  )

  const content = useMemo(
    () =>
      renderNodes.length > 0 ? (
        renderNodes
      ) : label && labelIndicator ? (
        <FlexInLine
          as="span"
          gap="xs-3"
          className="csg-tui-label-text-wrapper"
          sx={textWrapperSx}
        >
          {innerContent}
          {labelIndicator}
        </FlexInLine>
      ) : label ? (
        innerContent
      ) : null,
    [innerContent, label, labelIndicator, renderNodes]
  )

  const wrapperBaseStyles = useMemo(
    () => ({
      alignItems: 'center',
      cursor: readOnly ? 'auto' : disabled ? 'not-allowed' : 'pointer'
    }),
    [disabled, readOnly]
  )

  const iconBaseStyles = useMemo(
    () => ({
      height: iconSize,
      width: iconSize
    }),
    [iconSize]
  )

  return (
    <FlexPrivate
      as={renderNodes.length > 0 ? undefined : 'label'}
      className="csg-tui-checkbox"
      fontSize={fontSize}
      gap={gap}
      width={width}
      minWidth={minWidth}
      {...rootProps}
      __css={wrapperBaseStyles}
    >
      {labelLocation === 'left' && content}
      <CheckboxInputBase
        className={classNames('csg-tui-checkbox-input', className, {
          'is-active': active,
          'is-hover': hover,
          'is-read-only': readOnly
        })}
        variant={variant ? `checkbox.${variant}` : 'checkbox.default'}
        state={state}
        iconBg={iconBg}
        iconColor={iconColor}
        disableBg={disableBg}
        disableColor={disableColor}
        {...inputProps}
      />
      {variant === 'checkIcon' ? (
        <Icon
          icon={
            inputState.isIndeterminate
              ? 'remove'
              : inputState.isChecked
                ? 'selected'
                : 'select'
          }
          size={iconSize}
          secondaryColor={
            inputState.isDisabled
              ? resolveColor(theme, 'neutrals.17')
              : undefined
          }
          __css={iconBaseStyles}
        />
      ) : variant === 'classicSuite' ? (
        <FlexPrivate
          className="csg-tui-checkbox-control"
          {...checkboxProps}
          __css={controllerStyle}
          justifyContent="center"
        />
      ) : (
        <BoxPrivate
          className="csg-tui-checkbox-control"
          {...checkboxProps}
          __css={controllerStyle}
        >
          {clonedIcon}
        </BoxPrivate>
      )}
      {labelLocation === 'right' && content}
    </FlexPrivate>
  )
})

const MemoCheckbox = React.memo(CheckboxPrivate)

//@ts-ignore
const Checkbox: FC<CheckboxProps> & CheckboxComponents = React.forwardRef<
  HTMLInputElement,
  CheckboxProps
>(function Checkbox(props, ref) {
  return <MemoCheckbox ref={ref} {...props} />
})

Checkbox.Formik = formikField(Checkbox, { isCheckbox: true })

export default Checkbox
